// Adapters bridging the real backend `Parcel` shape (lib/api.ts) onto the
// Parcelio UI, which was originally designed against the richer mock data
// shape (lib/mock-data.ts). The backend is thinner than the old mock — see
// the per-field notes below and in the page components that use these.

import type { Parcel as ApiParcel } from "@/lib/api";
import type { ParcelStatus as LegacyStatus } from "@/lib/mock-data";

/** Human label for a backend parcel status. */
export const STATUS_LABELS: Record<ApiParcel["status"], string> = {
  intake: "Pending pickup",
  assigned: "Assigned",
  picked: "Picked up",
  in_transit: "In transit",
  delivered: "Delivered",
  failed: "Failed",
  cancelled: "Cancelled",
};

/**
 * Rough client-side progress approximation. The backend does not provide a
 * progress percentage or ETA — this is a clearly-labeled local estimate
 * derived from status only, not real backend data.
 */
export const STATUS_PROGRESS: Record<ApiParcel["status"], number> = {
  intake: 10,
  assigned: 25,
  picked: 45,
  in_transit: 70,
  delivered: 100,
  failed: 60,
  cancelled: 0,
};

/** Maps the new backend status onto the old mock's status enum, purely so we
 * can keep reusing <StatusPill> / statusLabel / statusTone from mock-data
 * without duplicating tone logic. */
const STATUS_TO_LEGACY: Record<ApiParcel["status"], LegacyStatus> = {
  intake: "pending",
  assigned: "pending",
  picked: "in_transit",
  in_transit: "in_transit",
  delivered: "delivered",
  failed: "exception",
  cancelled: "exception",
};

export function toLegacyStatus(status: ApiParcel["status"]): LegacyStatus {
  return STATUS_TO_LEGACY[status];
}

export function parcelProgress(status: ApiParcel["status"]): number {
  return STATUS_PROGRESS[status];
}

export function parcelStatusLabel(status: ApiParcel["status"]): string {
  return STATUS_LABELS[status];
}

/** amount_kobo -> naira, formatted. */
export function formatParcelValue(amountKobo: number): string {
  const naira = amountKobo / 100;
  const sign = naira < 0 ? "-" : "";
  return `${sign}₦${Math.abs(naira).toLocaleString("en-NG")}`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" });
}

export function initials(name: string | null | undefined): string {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0]?.toUpperCase())
    .slice(0, 2)
    .join("");
}
